import { createSlice } from '@reduxjs/toolkit';
import { showLoading, hideLoading } from 'react-redux-loading-bar';
import * as Api from '../../../api/emailCampaignAPI';
import * as CustomerApi from '../../../api/customerAPI';
import { getAllReferralCampaigns } from '../referralCampaign/referralCampaignAPI';

const emptyDraft = {
  name: '',
  subject: '',
  html: '',
  referralCampaignId: '',
  segment: { listId: null, condition: null, limit: null },
};

const emptyListDraft = { name: '', description: '' };

export const emailCampaignSlice = createSlice({
  name: 'emailCampaign',
  initialState: {
    campaigns: [],
    lists: [],
    referralCampaigns: [],
    templateRules: { rules: [], prompt: '' },
    templateErrors: [],
    listMembers: [],
    listDraft: emptyListDraft,
    selectedListId: null,
    listEditorOpen: false,
    memberPanelOpen: false,
    tab: 'campaigns',
    status: 'idle',
    selectedId: null,
    draft: emptyDraft,
    stats: {},
    editorOpen: false,
    sendPanelOpen: false,
    suppressionPanelOpen: false,
    testSentFor: null,
    successMessage: '',
    errorMessage: '',
  },
  reducers: {
    openEditor: (state, action) => {
      state.draft = action.payload ?? emptyDraft;
      state.selectedId = action.payload?.id ?? null;
      state.editorOpen = true;
      state.templateErrors = [];
    },
    closeEditor: (state) => {
      state.editorOpen = false;
      state.draft = emptyDraft;
    },
    setTemplateRules: (state, action) => {
      state.templateRules = action.payload;
    },
    setTemplateErrors: (state, action) => {
      state.templateErrors = action.payload;
    },
    updateDraft: (state, action) => {
      state.draft = { ...state.draft, ...action.payload };
      if ('html' in action.payload) {
        state.templateErrors = [];
      }
    },
    openSendPanel: (state, action) => {
      state.selectedId = action.payload;
      state.sendPanelOpen = true;
    },
    closeSendPanel: (state) => {
      state.sendPanelOpen = false;
      state.stats = {};
    },
    openSuppressionPanel: (state) => {
      state.suppressionPanelOpen = true;
    },
    closeSuppressionPanel: (state) => {
      state.suppressionPanelOpen = false;
    },
    setCampaigns: (state, action) => {
      state.campaigns = action.payload;
    },
    setTab: (state, action) => {
      state.tab = action.payload;
    },
    setLists: (state, action) => {
      state.lists = action.payload;
    },
    setReferralCampaigns: (state, action) => {
      state.referralCampaigns = action.payload;
    },
    setListMembers: (state, action) => {
      state.listMembers = action.payload;
    },
    setSelectedListId: (state, action) => {
      state.selectedListId = action.payload;
    },
    openListEditor: (state, action) => {
      state.listDraft = action.payload
        ? { name: action.payload.name, description: action.payload.description }
        : emptyListDraft;
      state.selectedListId = action.payload?.id ?? null;
      state.listEditorOpen = true;
    },
    closeListEditor: (state) => {
      state.listEditorOpen = false;
      state.listDraft = emptyListDraft;
    },
    updateListDraft: (state, action) => {
      state.listDraft = { ...state.listDraft, ...action.payload };
    },
    openMemberPanel: (state, action) => {
      state.selectedListId = action.payload;
      state.memberPanelOpen = true;
    },
    closeMemberPanel: (state) => {
      state.memberPanelOpen = false;
      state.listMembers = [];
    },
    setStats: (state, action) => {
      state.stats = action.payload;
    },
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setSelectedId: (state, action) => {
      state.selectedId = action.payload;
    },
    markTestSent: (state, action) => {
      state.testSentFor = action.payload;
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    setErrorMessage: (state, action) => {
      state.errorMessage = action.payload;
    },
  },
});

export const {
  setTab,
  setLists,
  setReferralCampaigns,
  setListMembers,
  setSelectedListId,
  openListEditor,
  closeListEditor,
  updateListDraft,
  openMemberPanel,
  closeMemberPanel,
  openEditor,
  closeEditor,
  updateDraft,
  openSendPanel,
  closeSendPanel,
  openSuppressionPanel,
  closeSuppressionPanel,
  setCampaigns,
  setStats,
  setStatus,
  setSelectedId,
  markTestSent,
  setSuccessMessage,
  setErrorMessage,
  setTemplateRules,
  setTemplateErrors,
} = emailCampaignSlice.actions;

const messageOf = (error) =>
  error?.response?.data?.message ?? error?.message ?? 'Something went wrong';

export const fetchCampaigns = () => async (dispatch) => {
  dispatch(showLoading());
  dispatch(setStatus('loading'));
  try {
    const response = await Api.getCampaigns();
    dispatch(setCampaigns(response.data));
    dispatch(setStatus('idle'));
  } catch (error) {
    dispatch(setStatus('failed'));
    dispatch(setErrorMessage(messageOf(error)));
  } finally {
    dispatch(hideLoading());
  }
};

export const saveCampaign = (id, campaign) => async (dispatch) => {
  dispatch(showLoading());
  dispatch(setTemplateErrors([]));
  try {
    if (id) {
      await Api.updateCampaign(id, campaign);
    } else {
      await Api.createCampaign(campaign);
    }
    dispatch(closeEditor());
    dispatch(setSuccessMessage('Campaign saved'));
    dispatch(fetchCampaigns());
  } catch (error) {
    dispatch(setTemplateErrors(error?.response?.data?.details ?? []));
    dispatch(setErrorMessage(messageOf(error)));
  } finally {
    dispatch(hideLoading());
  }
};

export const fetchTemplateRules = () => async (dispatch) => {
  try {
    const response = await Api.getTemplateRules();
    dispatch(setTemplateRules(response.data));
  } catch (error) {
    // The server still enforces the rules on save, so a missing panel must not block the editor.
  }
};

export const materialiseAudience = (id) => async (dispatch) => {
  dispatch(showLoading());
  try {
    const response = await Api.materialiseAudience(id);
    dispatch(setSuccessMessage(`Added ${response.data.added} recipient(s)`));
    dispatch(fetchStats(id));
  } catch (error) {
    dispatch(setErrorMessage(messageOf(error)));
  } finally {
    dispatch(hideLoading());
  }
};

export const raiseCap = (id, limit) => async (dispatch) => {
  dispatch(showLoading());
  try {
    const response = await Api.raiseCampaignCap(id, limit);
    dispatch(setSuccessMessage(`Cap raised to ${limit}: added ${response.data.added} new recipient(s)`));
    return true;
  } catch (error) {
    dispatch(setErrorMessage(messageOf(error)));
    return false;
  } finally {
    // A failed build can still have raised the cap and reopened the campaign.
    dispatch(fetchCampaigns());
    dispatch(fetchStats(id));
    dispatch(hideLoading());
  }
};

export const sendTest = (id, email, firstName) => async (dispatch) => {
  dispatch(showLoading());
  try {
    await Api.sendTestCampaign(id, email, firstName);
    dispatch(markTestSent(id));
    dispatch(setSuccessMessage(`Test sent to ${email}`));
  } catch (error) {
    dispatch(setErrorMessage(messageOf(error)));
  } finally {
    dispatch(hideLoading());
  }
};

export const send = (id) => async (dispatch) => {
  dispatch(showLoading());
  try {
    await Api.sendCampaign(id);
    dispatch(setSuccessMessage('Campaign sending'));
    dispatch(fetchCampaigns());
    dispatch(fetchStats(id));
  } catch (error) {
    dispatch(setErrorMessage(messageOf(error)));
  } finally {
    dispatch(hideLoading());
  }
};

export const pause = (id) => async (dispatch) => {
  dispatch(showLoading());
  try {
    await Api.pauseCampaign(id);
    dispatch(setSuccessMessage('Campaign paused'));
    dispatch(fetchCampaigns());
  } catch (error) {
    dispatch(setErrorMessage(messageOf(error)));
  } finally {
    dispatch(hideLoading());
  }
};

export const fetchStats = (id) => async (dispatch) => {
  try {
    const response = await Api.getCampaignStats(id);
    dispatch(setStats(response.data));
  } catch (error) {
    dispatch(setErrorMessage(messageOf(error)));
  }
};

export const removeSuppression = (email) => async (dispatch) => {
  dispatch(showLoading());
  try {
    await Api.removeSuppression(email);
    dispatch(setSuccessMessage(`${email} can receive campaigns again`));
    dispatch(closeSuppressionPanel());
  } catch (error) {
    dispatch(setErrorMessage(messageOf(error)));
  } finally {
    dispatch(hideLoading());
  }
};

// Referral campaigns live in parkit-server, not this service. The picker needs them so the
// author chooses a campaign rather than typing an id nobody can verify.
export const fetchReferralCampaigns = () => async (dispatch) => {
  try {
    const response = await getAllReferralCampaigns(0, 100, 'desc', 'startDate');
    dispatch(setReferralCampaigns(response.data?.content ?? []));
  } catch (error) {
    // A campaign is still sendable without a terms link, so this must not block the editor.
    dispatch(setReferralCampaigns([]));
  }
};

export const fetchLists = () => async (dispatch) => {
  dispatch(showLoading());
  try {
    const response = await Api.getAudienceLists();
    dispatch(setLists(response.data));
  } catch (error) {
    dispatch(setErrorMessage(messageOf(error)));
  } finally {
    dispatch(hideLoading());
  }
};

export const saveList = (id, list) => async (dispatch) => {
  dispatch(showLoading());
  try {
    if (id) {
      await Api.updateAudienceList(id, list);
    } else {
      await Api.createAudienceList(list);
    }
    dispatch(closeListEditor());
    dispatch(setSuccessMessage('Audience list saved'));
    dispatch(fetchLists());
  } catch (error) {
    dispatch(setErrorMessage(messageOf(error)));
  } finally {
    dispatch(hideLoading());
  }
};

export const deleteList = (id) => async (dispatch) => {
  dispatch(showLoading());
  try {
    await Api.deleteAudienceList(id);
    dispatch(setSelectedListId(null));
    dispatch(setSuccessMessage('Audience list deleted'));
    dispatch(fetchLists());
  } catch (error) {
    dispatch(setErrorMessage(messageOf(error)));
  } finally {
    dispatch(hideLoading());
  }
};

// fetchListMembers resolves ids to contacts through parkit-server: the campaign service stores
// no addresses, so a bare id list is unreadable on its own.
export const fetchListMembers = (id, resolveContacts = true) => async (dispatch) => {
  dispatch(showLoading());
  try {
    const response = await Api.getAudienceListMembers(id);
    const userIds = response.data.userIds ?? [];
    if (userIds.length === 0) {
      dispatch(setListMembers([]));
      return;
    }

    if (!resolveContacts) {
      dispatch(setListMembers(userIds.map((userId) => ({ userId }))));
      return;
    }

    const resolved = await CustomerApi.resolveAudience(userIds);
    const byId = new Map(resolved.data.map((member) => [member.userId, member]));
    dispatch(
      setListMembers(
        userIds.map((userId) => byId.get(userId) ?? { userId, email: null, firstName: null })
      )
    );
  } catch (error) {
    dispatch(setErrorMessage(messageOf(error)));
  } finally {
    dispatch(hideLoading());
  }
};

export const addListMembers = (id, userIds) => async (dispatch) => {
  dispatch(showLoading());
  try {
    const response = await Api.addAudienceListMembers(id, userIds);
    dispatch(setSuccessMessage(`Added ${response.data.added} member(s)`));
    dispatch(fetchListMembers(id));
    dispatch(fetchLists());
  } catch (error) {
    dispatch(setErrorMessage(messageOf(error)));
  } finally {
    dispatch(hideLoading());
  }
};

export const removeListMember = (id, userId) => async (dispatch) => {
  dispatch(showLoading());
  try {
    await Api.removeAudienceListMember(id, userId);
    dispatch(fetchListMembers(id));
    dispatch(fetchLists());
  } catch (error) {
    dispatch(setErrorMessage(messageOf(error)));
  } finally {
    dispatch(hideLoading());
  }
};

export const selectState = (state) => state.emailCampaign;
export const selectLists = (state) => state.emailCampaign.lists;
export const selectReferralCampaigns = (state) => state.emailCampaign.referralCampaigns;
export const selectTemplateRules = (state) => state.emailCampaign.templateRules;
export const selectTemplateErrors = (state) => state.emailCampaign.templateErrors;
export const selectListDraft = (state) => state.emailCampaign.listDraft;
export const selectListMembers = (state) => state.emailCampaign.listMembers;
export const selectSelectedListId = (state) => state.emailCampaign.selectedListId;
export const selectCampaigns = (state) => state.emailCampaign.campaigns;
export const selectDraft = (state) => state.emailCampaign.draft;
export const selectStats = (state) => state.emailCampaign.stats;
export const selectSelectedId = (state) => state.emailCampaign.selectedId;
export const selectEditorOpen = (state) => state.emailCampaign.editorOpen;
export const selectSendPanelOpen = (state) => state.emailCampaign.sendPanelOpen;
export const selectSuppressionPanelOpen = (state) => state.emailCampaign.suppressionPanelOpen;
export const selectTestSentFor = (state) => state.emailCampaign.testSentFor;

export default emailCampaignSlice.reducer;
